import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import TableSkeleton from "../components/TableSkeleton";
import AssessmentMatchModal from "../components/AssessmentMatchModal";
import AiAssessUpsellModal from "../components/AiAssessUpsellModal";
import BulkAiAssessModal from "../components/BulkAiAssessModal";
import ProductModal from "../components/ProductModal/ProductModal";
import AssessMenu from "../components/AssessMenu";
import {
  getProductsCacheKey as getCacheKey,
  clearSupplierCache,
} from "../utils/productListCache";
import { computeAiDataReadiness } from "../utils/aiDataReadiness";
import { useConfirm } from "../components/ConfirmDialog";
import "./ProductList.css";
import zilchLogoBlack from "../assets/logos/zilchlogoblack.png";
//test

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "/api";

const ITEMS_PER_PAGE = 50;

// How many products one bulk AI assessment run covers — one full page of the
// list. Each product is an AI call (two in parts mode), so the modal sends them
// in small chunks rather than one long request. A selection bigger than this
// just runs in batches; the modal says how many are left over.
const BULK_AI_MAX_PER_RUN = ITEMS_PER_PAGE;

// Cache expiry time (5 minutes)
const CACHE_EXPIRY_MS = 5 * 60 * 1000;

// Matches the modal's LOOSE_PACKED_VALUE_RE — a packaging string meaning "no
// packaging" still counts as having answered the packaging question.
const LOOSE_PACKED_VALUE_RE = /^loose\s*pack(ed)?\.?\s*$/i;

// Resolve a product's AI-data readiness the SAME way the product modal does, so
// the list badge and the modal always agree. The modal works from its live form
// (prefilled from product columns falling back to pcf_inputs); here we resolve
// the same four fields straight from the product + pcf_inputs and feed them to
// the shared rules in computeAiDataReadiness. Keep this resolution in sync with
// the modal's form prefill in ProductModal.js.
function getProductDataReadiness(product) {
  const pcf = product?.pcf_inputs || {};

  const weight = product?.weight_grams != null ? product.weight_grams : pcf.weight_g;

  // Materials: structured rows (modal needs category + subcategory) or, when
  // there are none, the imported/legacy free-text materials.
  const materialRows = (Array.isArray(pcf.materials) ? pcf.materials : []).filter((m) => m && m.category);
  const legacyMaterialsText = materialRows.length === 0
    ? (product?.source_metadata?.material || pcf.materials_text || "")
    : "";
  const hasMaterials = materialRows.some((m) => m.category && m.subcategory) || !!legacyMaterialsText;

  // Origin: country of origin or a supplier location the estimator can geocode.
  const hasOrigin = !!(product?.country_of_origin || pcf.origin_country)
    || !!(product?.source_metadata?.origin_supplier_location || pcf.origin_address);

  // Packaging: explicitly loose-packed, a structured item, or a packaging value.
  const loosePacked = !!pcf.no_packaging || LOOSE_PACKED_VALUE_RE.test(product?.packaging || "");
  const pcfPackaging = (Array.isArray(pcf.packaging) ? pcf.packaging : []).filter((p) => p && p.type);
  const hasPackaging = loosePacked || pcfPackaging.length > 0 || !!product?.packaging;

  return computeAiDataReadiness({ weight, hasMaterials, hasOrigin, hasPackaging });
}

function ProductList() {
  const { supplierInfo, getToken, billing } = useAuth();
  const { confirm, alert } = useConfirm();
  const navigate = useNavigate();  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [unlinking, setUnlinking] = useState(false);
  const [searchInput, setSearchInput] = useState(""); // Local input value
  const [searchQuery, setSearchQuery] = useState(""); // Actual search query for API
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedSubcategory, setSelectedSubcategory] = useState("all");
  const [selectedFootprint, setSelectedFootprint] = useState("all"); // 'all' | 'true' | 'false'
  const [selectedNeedsData, setSelectedNeedsData] = useState(false); // filter to products missing weight/materials
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  // Categories used by this supplier (from /api/products/promo-categories).
  // Each entry: { id, slug, name, is_other, subcategories: [{ id, slug, name }] }.
  const [supplierPromoCategories, setSupplierPromoCategories] = useState([]);
  // Distinct supplier_category strings for non-promo orgs (their own categories).
  const [supplierCategories, setSupplierCategories] = useState([]);
  const searchDebounceTimer = useRef(null);
  const hasLoadedInitialData = useRef(false);
  // An exact count makes the server scan and count every row matching the
  // filters, on top of fetching the page. It only changes when the filters
  // change — and every filter change already resets to page 1 — so we ask for
  // it once per filter set and carry it across page changes instead. `sig`
  // records which filter set the held count belongs to.
  const countRef = useRef({ sig: null, count: 0, pages: 1 });
  const [showProductModal, setShowProductModal] = useState(false);
  const [productModalMode, setProductModalMode] = useState('create');
  const [editingProduct, setEditingProduct] = useState(null);
  // When true, the product modal opens with a prominent "add the missing data"
  // prompt — set when an auto-estimate can't run because required data (weight)
  // is missing, so the user lands on the fix instead of an off-screen error.
  const [productModalNeedsData, setProductModalNeedsData] = useState(false);
  const [promoCategories, setPromoCategories] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [showAiAssessModal, setShowAiAssessModal] = useState(false);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [csvError, setCsvError] = useState('');
  const [csvAutoEstimating, setCsvAutoEstimating] = useState(false);
  const [csvAutoEstimateResult, setCsvAutoEstimateResult] = useState(null);
  const [csvAutoEstimateError, setCsvAutoEstimateError] = useState('');
  const [autoEstimatingId, setAutoEstimatingId] = useState(null);
  const [showBulkAiModal, setShowBulkAiModal] = useState(false);

  const assessmentLimitRaw = Number(billing?.assessment_limit);
  const hasAssessmentCap = Number.isFinite(assessmentLimitRaw) && assessmentLimitRaw > 0;
  const assessmentUsed = Number(billing?.assessment_used ?? billing?.assessed_sku_used ?? 0);
  const assessmentLimitReached = hasAssessmentCap ? assessmentUsed >= assessmentLimitRaw : false;
  const isFreePlan = billing?.plan === 'free' && !billing?.is_override;
  const canEditProducts = billing?.is_override || (billing?.plan && billing.plan !== 'free');
  const canDeleteProducts = billing?.is_override
    ? true
    : billing?.can_delete_products !== undefined
      ? billing.can_delete_products
      : !isFreePlan;
  const canUseCsvImport = billing?.is_override || (billing?.plan && billing.plan !== 'free');
  // AI parts-based estimates are a Team+ entitlement (two model calls per
  // product vs one). Mirrors can_ai_parts_assessment in the plan matrix.
  const canUsePartsAiAssessment = billing?.is_override || !!billing?.can_ai_parts_assessment;
  const isDistributor = supplierInfo?.account_type === 'Distributor';
  const isSupplier = supplierInfo?.account_type === 'Supplier';
  // Promo orgs use the shared promo taxonomy; non-promo orgs (e.g. Bunzl) use
  // their own supplier_category/subcategory. Default to promo until supplierInfo
  // loads so existing behaviour is preserved for everyone else.
  const usesPromoTaxonomy = (supplierInfo?.vertical ?? 'promo') === 'promo';
  const [pcfRequests, setPcfRequests] = useState([]);
  const [pcfRequestsLoading, setPcfRequestsLoading] = useState(false);
  const [showRequestedPcf, setShowRequestedPcf] = useState(false);

  // Distributor: ask the source supplier to run the PCF for a linked product.
  // Mirrors the "Request PCF" flow in SupplierCatalogue, minus the
  // add-to-catalogue step — a linked product is already in the catalogue.
  const [pcfRequestProduct, setPcfRequestProduct] = useState(null);
  const [pcfRequestQuantity, setPcfRequestQuantity] = useState("");
  const [pcfRequestReason, setPcfRequestReason] = useState("");
  const [pcfRequestSubmitting, setPcfRequestSubmitting] = useState(false);
  const [pcfRequestError, setPcfRequestError] = useState("");
  const [pcfRequestSuccess, setPcfRequestSuccess] = useState("");

  // Which row's "Assess" popover is open, and the button it hangs off.
  const [assessMenu, setAssessMenu] = useState(null); // { productId, anchorEl }

  // Handle supplier ID input and fetch products
  const fetchProductsBySupplier = async (supplierIdParam, pageParam = 1, searchParam = "", categoryParam = "all", subcategoryParam = "all", footprintParam = "all", useCache = true) => {
    // selectedNeedsData is read from state (not a param) so existing call
    // sites stay unchanged; the fetch effect re-runs when it toggles.
    const cacheKey = getCacheKey(supplierIdParam, pageParam, searchParam, categoryParam, subcategoryParam, footprintParam, selectedNeedsData ? "true" : "");

    // Everything the count depends on, minus the page.
    const filterSig = [
      supplierIdParam, searchParam, categoryParam, subcategoryParam, footprintParam,
      selectedNeedsData ? "1" : "0",
    ].join("|");
    // Recount when the filter set is new, and whenever the caller bypassed the
    // cache — that's every mutation-triggered refetch, where the held count is
    // exactly what may have gone stale.
    const needCount = countRef.current.sig !== filterSig || !useCache;

    // Check cache first if enabled
    if (useCache) {
      try {
        const cachedData = sessionStorage.getItem(cacheKey);
        if (cachedData) {
          const parsed = JSON.parse(cachedData);
          const now = Date.now();

          // Check if cache is still valid (not expired)
          if (parsed.timestamp && (now - parsed.timestamp) < CACHE_EXPIRY_MS) {
            // Use cached data immediately
            setProducts(parsed.data || []);
            setTotalPages(Number(parsed.total_pages || 1));
            setTotalCount(Number(parsed.item_count || 0));
            // Seed the carried count so a later uncounted page fetch has a
            // fallback even when this page came from the cache.
            countRef.current = {
              sig: filterSig,
              count: Number(parsed.item_count || 0),
              pages: Number(parsed.total_pages || 1),
            };
            setError("");

            // If we have cached data, fetch in background to refresh (but don't show loading)
            // Only do this if we haven't loaded initial data yet or if it's been a while
            if (!hasLoadedInitialData.current || (now - parsed.timestamp) > (CACHE_EXPIRY_MS / 2)) {
              // Fetch in background without showing loading state
              fetchProductsBySupplier(supplierIdParam, pageParam, searchParam, categoryParam, subcategoryParam, footprintParam, false);
            }
            return;
          } else {
            // Cache expired, remove it
            sessionStorage.removeItem(cacheKey);
          }
        }
      } catch (err) {
        console.warn("Error reading cache:", err);
        // Continue to fetch if cache read fails
      }
    }

    // No cache or cache expired, fetch from API
    if (!useCache) {
      // Background refresh - don't show loading
      setLoading(false);
    } else {
      setLoading(true);
    }
    setError("");

    try {
      const params = {
        // Prefer singular param to avoid array encoding
        supplier_id: supplierIdParam,
        page: pageParam,
        items_per_page: ITEMS_PER_PAGE,
        include_discontinued: false,
        search: searchParam,
        lean: true,
        with_count: needCount,
      };

      // Category filter. Promo orgs filter by promo taxonomy UUIDs; non-promo
      // orgs filter by their own supplier_category text.
      if (categoryParam && categoryParam !== "all") {
        if (usesPromoTaxonomy) params.promo_category = categoryParam;
        else params.category = categoryParam;
      }
      // Promo subcategory filter (UUID) — promo orgs only.
      if (subcategoryParam && subcategoryParam !== "all" && usesPromoTaxonomy) {
        params.promo_subcategory = subcategoryParam;
      }
      // Carbon footprint filter
      if (footprintParam === "true" || footprintParam === "false") {
        params.has_footprint = footprintParam;
      }
      // Products missing data required for an AI assessment
      if (selectedNeedsData) {
        params.needs_data = "true";
      }

      const endpoint = isDistributor ? `${API_BASE_URL}/distributor/catalogue` : `${API_BASE_URL}/products`;
      const response = await axios.get(endpoint, { params });
      const payload = response.data || {};
      const productsData = payload.data || [];
      // A null count means the server skipped it because we already had one.
      const rawCount = isDistributor ? payload.count : payload.item_count;
      const gotCount = rawCount !== null && rawCount !== undefined;
      const carried = countRef.current.sig === filterSig ? countRef.current : null;
      const totalCountData = gotCount
        ? Number(rawCount)
        : (carried ? carried.count : productsData.length);
      const totalPagesData = gotCount
        ? (isDistributor
            ? Math.max(1, Math.ceil(totalCountData / ITEMS_PER_PAGE))
            : Number(payload.total_pages || 1))
        : (carried ? carried.pages : 1);
      countRef.current = { sig: filterSig, count: totalCountData, pages: totalPagesData };

      setProducts(productsData);
      setTotalPages(totalPagesData);
      setTotalCount(totalCountData);

      // Cache the results
      try {
        const cacheData = {
          data: productsData,
          total_pages: totalPagesData,
          item_count: totalCountData,
          timestamp: Date.now(),
        };
        sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
      } catch (err) {
        console.warn("Error saving to cache:", err);
      }
      
      hasLoadedInitialData.current = true;
    } catch (err) {
      setError(
        "Failed to fetch products. Please check the supplier ID and try again."
      );
      console.error("Error fetching products:", err);
    } finally {
      setLoading(false);
    }
  };

  const updateCurrentCache = (nextProducts) => {
    const supplierId = supplierInfo?.supplier_id;
    if (!supplierId) return;
    const cacheKey = getCacheKey(supplierId, page, searchQuery, selectedCategory, selectedSubcategory, selectedFootprint, selectedNeedsData ? "true" : "");
    try {
      const cachedData = sessionStorage.getItem(cacheKey);
      if (!cachedData) return;
      const parsed = JSON.parse(cachedData);
      const cacheData = {
        ...parsed,
        data: nextProducts,
        timestamp: Date.now(),
      };
      sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (err) {
      console.warn("Error updating cache:", err);
    }
  };

  // Fetch promo categories + subcategories that have at least one product
  // for this supplier.
  const fetchCategories = async (supplierIdParam) => {
    // Non-promo orgs (e.g. Bunzl) filter by their own distinct supplier_category
    // values rather than the promo taxonomy.
    if (!usesPromoTaxonomy) {
      try {
        const resp = await axios.get(`${API_BASE_URL}/products/categories`, {
          params: { supplier_id: supplierIdParam },
        });
        setSupplierCategories(Array.isArray(resp.data?.data) ? resp.data.data : []);
      } catch (err) {
        console.error("Error fetching supplier categories:", err);
      }
      return;
    }

    const categoriesCacheKey = `promo_categories_cache_${supplierIdParam}`;

    // Check cache first
    try {
      const cachedData = sessionStorage.getItem(categoriesCacheKey);
      if (cachedData) {
        const parsed = JSON.parse(cachedData);
        const now = Date.now();

        if (parsed.timestamp && (now - parsed.timestamp) < CACHE_EXPIRY_MS) {
          setSupplierPromoCategories(parsed.data || []);
          return;
        } else {
          sessionStorage.removeItem(categoriesCacheKey);
        }
      }
    } catch (err) {
      console.warn("Error reading categories cache:", err);
    }

    try {
      // Categories present in this org's own catalogue. /products/promo-categories
      // auto-scopes to the authenticated org, so it's correct for both suppliers
      // and distributors viewing their own list. (The public /distributor/directory
      // endpoint is only for browsing *another* supplier's public catalogue and
      // 404s on your own non-public org.)
      const response = await axios.get(`${API_BASE_URL}/products/promo-categories`, {
        params: { supplier_id: supplierIdParam }
      });
      if (response.data && response.data.data) {
        const categoriesData = response.data.data;
        setSupplierPromoCategories(categoriesData);

        try {
          const cacheData = {
            data: categoriesData,
            timestamp: Date.now(),
          };
          sessionStorage.setItem(categoriesCacheKey, JSON.stringify(cacheData));
        } catch (err) {
          console.warn("Error saving categories to cache:", err);
        }
      }
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  // Initial load: check auth and fetch categories
  useEffect(() => {
    const storedSupplierId = supplierInfo?.supplier_id;
    if (!storedSupplierId) {
      return;
    }
    // Fetch all categories once on mount
    fetchCategories(storedSupplierId);

    fetch(`${API_BASE_URL}/promo-categories`)
      .then((r) => r.json())
      .then((d) => setPromoCategories(d.data || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierInfo]);

  // Fetch PCF requests for suppliers
  useEffect(() => {
    if (!isSupplier || !supplierInfo?.supplier_id) return;
    let cancelled = false;
    setPcfRequestsLoading(true);
    getToken().then(token => {
      if (!token || cancelled) return;
      return axios.get(`${API_BASE_URL}/supplier/pcf-requests`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(res => {
        if (!cancelled) setPcfRequests(res.data?.data || []);
      }).catch(() => {});
    }).finally(() => {
      if (!cancelled) setPcfRequestsLoading(false);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierInfo, isSupplier]);

  // Refetch products when page, search, category, subcategory, footprint, or supplierInfo changes
  useEffect(() => {
    const storedSupplierId = supplierInfo?.supplier_id;
    if (!storedSupplierId) return;
    fetchProductsBySupplier(storedSupplierId, page, searchQuery, selectedCategory, selectedSubcategory, selectedFootprint);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchQuery, selectedCategory, selectedSubcategory, selectedFootprint, selectedNeedsData, supplierInfo]);

  // Clear selections when any filter changes
  useEffect(() => {
    setSelectedProducts(new Set());
  }, [page, searchQuery, selectedCategory, selectedSubcategory, selectedFootprint, selectedNeedsData]);

  const hasViewableResults = (p) => {
    const step = Number(p?.lca?.current_step || 0);
    const totalEmissions = p?.lca?.total_emissions_kg;
    return step >= 5 && totalEmissions !== null && totalEmissions !== undefined;
  };

  const hasStartedAssessment = (p) => {
    const step = Number(p?.lca?.current_step || 0);
    return step > 0;
  };

  const shouldShowUpgradeForProduct = (p) => {
    return !hasViewableResults(p) && !hasStartedAssessment(p) && assessmentLimitReached;
  };

  // ââ Merged Carbon Footprint / Assessment cell âââââââââââââââââââââââ
  // One column carries the whole assessment story for a product: the footprint
  // once it exists, otherwise the way to get one. Everything that starts an
  // assessment lives behind the "Assess" popover, so the cell stays a single
  // control instead of a row of competing buttons.

  const resultsPathFor = (product) => {
    const ownerId = product.link_status === "linked"
      ? product.source_supplier?.id
      : supplierInfo?.supplier_id;
    return `/product-results/${product.id}?supplier_id=${ownerId}`;
  };

  const openAssessMenu = (e, product) => {
    e.stopPropagation();
    const anchorEl = e.currentTarget;
    setAssessMenu((prev) =>
      prev?.productId === product.id ? null : { productId: product.id, anchorEl },
    );
  };

  const closeAssessMenu = () => setAssessMenu(null);

  // Manual route: an owned product goes straight to the calculator; a linked one
  // has to be forked into the distributor's own catalogue first.
  const handleManualAssess = (product) => {
    if (product.link_status === "linked") {
      handleForkLinkedProduct(product);
      return;
    }
    navigate(`/lca-calculator/${product.id}`, {
      state: { product: getMinimalProduct(product) },
    });
  };

  // One product's assessment state, resolved once so the chip and its menu can
  // never disagree. A linked product's assessment belongs to the supplier, so
  // from the distributor's side anything short of a published footprint is not
  // theirs to continue â hence the separate "waiting" state.
  const getAssessState = (product) => {
    const step = Number(product?.lca?.current_step || 0);
    const total = Number(product?.lca?.total_emissions_kg);
    const linked = product.link_status === "linked";
    const done = hasViewableResults(product);
    let kind = "none";
    if (done) kind = "done";
    else if (linked) kind = product.pcf_requested ? "requested" : (step > 0 ? "waiting" : "none");
    else if (product?.lca?.is_ai_estimated && step > 0) kind = "draft";
    else if (step > 0) kind = "progress";
    return {
      kind,
      step,
      total: Number.isFinite(total) && total > 0 ? total : null,
      linked,
      supplierName: product.source_supplier?.name || "the supplier",
    };
  };

  const buildAssessMenuItems = (product) => {
    const { kind, linked, supplierName } = getAssessState(product);
    const items = [];
    // The allowance gate only bites on a product that has never been assessed,
    // so re-assessing something already counted is never blocked.
    const capped = shouldShowUpgradeForProduct(product);
    const upgradeBadge = capped ? "Upgrade" : null;
    const goBilling = () => navigate("/billing");
    const openCalculator = () =>
      navigate(`/lca-calculator/${product.id}`, {
        state: { product: getMinimalProduct(product) },
      });

    // Whatever the chip's own click does, spelled out â the menu is then a
    // complete picture of the options rather than a list of the leftovers.
    if (kind === "done") {
      items.push({
        key: "results",
        label: "View results",
        hint: linked ? `Assessed by ${supplierName}` : "Open the full breakdown",
        onSelect: () =>
          navigate(resultsPathFor(product), {
            state: { product: getMinimalProduct(product) },
          }),
      });
    } else if (kind === "draft") {
      items.push({
        key: "review",
        label: "Review & approve",
        hint: "Check the AI estimate and publish it.",
        onSelect: openCalculator,
      });
    } else if (kind === "progress") {
      items.push({
        key: "continue",
        label: "Continue assessment",
        hint: "Pick up where you left off.",
        onSelect: openCalculator,
      });
    }

    // Asking the supplier is the route we want distributors to take: it costs
    // them nothing, and it is what pulls the supplier onto Zilch.
    if (linked && isDistributor && kind !== "done") {
      items.push(
        kind === "requested"
          ? {
              key: "requested",
              label: "Requested from supplier",
              hint: `Waiting on ${supplierName}. The footprint appears here as soon as they publish it.`,
              disabled: true,
            }
          : {
              key: "request",
              tone: "primary",
              label: `Ask ${supplierName}`,
              hint: "Request their own PCF — real data, and it uses none of your allowance.",
              onSelect: () => openPcfRequestModal(product),
            },
      );
    }

    const started = kind === "done" || kind === "draft" || kind === "progress";
    items.push({
      key: "ai",
      label: started ? "Re-estimate with AI" : "Estimate with AI",
      badge: upgradeBadge,
      hint: capped
        ? "Your plan's assessed-product allowance is used up."
        : linked
          ? "Copies it into your catalogue, then estimates from its data."
          : "Zilch estimates the footprint from this product's data.",
      onSelect: capped ? goBilling : () => handleAutoEstimateProduct(product),
    });

    items.push({
      key: "manual",
      label: started ? "Edit manually" : "Assess manually",
      badge: upgradeBadge,
      hint: capped
        ? "Upgrade to assess more products."
        : linked
          ? "Copies it into your catalogue, then opens the calculator."
          : "Step through the calculator yourself.",
      onSelect: capped ? goBilling : () => handleManualAssess(product),
    });

    return items;
  };

  // Every row renders the SAME control — a fixed-width chip with a status dot,
  // a label and a caret — so the column scans as one column instead of a mix of
  // buttons at different widths. Only the dot colour and the label change with
  // state: the label click does the obvious thing, the caret opens the menu.
  const renderAssessmentCell = (product) => {
    const { kind, step, total, supplierName } = getAssessState(product);
    const menuOpen = assessMenu?.productId === product.id;

    if (autoEstimatingId === product.id) {
      return (
        <span className="assess-chip assess-chip--pending">
          <span className="assess-chip__main assess-chip__main--static">
            <span className="assess-chip__dot" />
            <span className="assess-chip__label">Estimating…</span>
          </span>
        </span>
      );
    }

    const view = {
      done: {
        tone: "done",
        label: total !== null ? `${total.toFixed(3)} kg CO₂e` : "Calculated",
        title: "View results",
      },
      draft: {
        tone: "pending",
        label: total !== null ? `~${total.toFixed(3)} kg CO₂e` : "AI draft",
        title: "AI estimate — review and approve it",
      },
      progress: {
        tone: "pending",
        label: `Step ${step} of 5`,
        title: "Continue this assessment",
      },
      requested: {
        tone: "pending",
        label: "Requested",
        title: `Requested from ${supplierName}`,
      },
      waiting: {
        tone: "pending",
        label: "Supplier assessing",
        title: `${supplierName} has an assessment in progress`,
      },
      none: {
        tone: "empty",
        label: "Not assessed",
        title: "Choose how to assess this product",
      },
    }[kind];

    // The label leads somewhere for the states the user can act on directly;
    // otherwise it just opens the same menu as the caret.
    const linkTo = kind === "done"
      ? resultsPathFor(product)
      : kind === "draft" || kind === "progress"
        ? `/lca-calculator/${product.id}`
        : null;

    return (
      <span className={`assess-chip assess-chip--${view.tone}${menuOpen ? " assess-chip--open" : ""}`}>
        {linkTo ? (
          <Link
            to={linkTo}
            state={{ product: getMinimalProduct(product) }}
            className="assess-chip__main"
            title={view.title}
          >
            <span className="assess-chip__dot" />
            <span className="assess-chip__label">{view.label}</span>
          </Link>
        ) : (
          <button
            type="button"
            className="assess-chip__main"
            title={view.title}
            onClick={(e) => openAssessMenu(e, product)}
          >
            <span className="assess-chip__dot" />
            <span className="assess-chip__label">{view.label}</span>
          </button>
        )}
        <button
          type="button"
          className="assess-chip__caret"
          onClick={(e) => openAssessMenu(e, product)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label="Assessment options"
          title="Assessment options"
        >
          <span aria-hidden="true">▾</span>
        </button>
      </span>
    );
  };

  const handlePrev = () => setPage((p) => Math.max(1, p - 1));
  const handleNext = () => setPage((p) => Math.min(totalPages || 1, p + 1));
  
  // Handle search change with debounce - reset to page 1
  const handleSearchChange = (value) => {
    // Update input immediately for responsive UI
    setSearchInput(value);
    
    // Clear any existing timer
    if (searchDebounceTimer.current) {
      clearTimeout(searchDebounceTimer.current);
    }
    
    // Set new timer to update actual search query after 500ms
    searchDebounceTimer.current = setTimeout(() => {
      setSearchQuery(value);
      setPage(1);
    }, 500);
  };
  
  // Handle category change - reset subcategory + page
  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    setSelectedSubcategory("all");
    setPage(1);
  };

  const handleSubcategoryChange = (value) => {
    setSelectedSubcategory(value);
    setPage(1);
  };

  const handleFootprintChange = (value) => {
    setSelectedFootprint(value);
    setPage(1);
  };

  // Category list to render: prefer the supplier-scoped list (only categories
  // that have at least one product), but fall back to the full taxonomy if
  // the supplier hasn't categorised any products yet (promo_category_id null).
  const categoryOptions = supplierPromoCategories.length > 0
    ? supplierPromoCategories
    : promoCategories;

  // Subcategories available for the selected category (or empty when "all").
  const availableSubcategories = (() => {
    if (selectedCategory === "all") return [];
    const cat = categoryOptions.find((c) => c.id === selectedCategory);
    return cat?.subcategories || [];
  })();

  const hasActiveFilters =
    searchInput.trim() !== "" ||
    selectedCategory !== "all" ||
    selectedSubcategory !== "all" ||
    selectedFootprint !== "all" ||
    selectedNeedsData;

  const clearAllFilters = () => {
    setSearchInput("");
    if (searchDebounceTimer.current) clearTimeout(searchDebounceTimer.current);
    setSearchQuery("");
    setSelectedCategory("all");
    setSelectedSubcategory("all");
    setSelectedFootprint("all");
    setSelectedNeedsData(false);
    setPage(1);
  };

  // Helper to create minimal product object for routing (only what's needed)
  const getMinimalProduct = (product) => ({
    id: product.id,
    name: product.name,
    code: product.code,
    weight_grams: product.weight_grams,
    details: product.details,
    country_of_origin: product.country_of_origin || null,
    images: product.images,
    supplier_category: product.supplier_category,
    supplier_subcategory: product.supplier_subcategory,
    description: product.description,
    packaging: product.packaging || null,
    source_metadata: product.source_metadata || null,
  });

  const generateCsvTemplate = () => {
  const headers = [
    'name',
    'sku',
    'description',
    'weight_grams',
    'quality',
    'packaging',
    'packaging_weight_grams',
    'materials',
    'category',
    'subcategory',
    'origin_supplier_location',
    'final_destination',
    'transport_mode',
    'distance_traveled_km',
    'decorations',
    'decoration_type',
    'image_url',
  ];

  const example = [
    'Eco Tote Bag',
    'ABC123',
    '100% organic cotton tote bag',
    '120',
    'Virgin',
    'Recycled cardboard box 90% + 10% recycled paper filler',
    '50',
    '100% organic cotton',
    'Bags',
    'Tote Bags',
    'No. 18 Gangkou Road Beilun District Ningbo Zhejiang China 315800',
    '102 Victoria St Carlton VIC 3001',
    'Ocean freight',
    '8050',
    'Yes',
    'Embroidery',
    'https://example.com/image.jpg',
  ];

  const escapeCsvValue = (value) => {
    const stringValue = String(value ?? '');
    return `"${stringValue.replace(/"/g, '""')}"`;
  };

  const csv = [
    headers.map(escapeCsvValue).join(','),
    example.map(escapeCsvValue).join(','),
  ].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

  return URL.createObjectURL(blob);
};

  const closeCsvModal = () => {
    setShowCsvModal(false);
    setCsvFile(null);
    setCsvResult(null);
    setCsvError('');
    setCsvAutoEstimating(false);
    setCsvAutoEstimateResult(null);
    setCsvAutoEstimateError('');
  };

  const handleCsvAutoEstimate = async () => {
    const importedIds = (csvResult?.data || []).map((p) => p.id).filter(Boolean);
    if (importedIds.length === 0) return;
    setCsvAutoEstimating(true);
    setCsvAutoEstimateError('');
    setCsvAutoEstimateResult(null);
    try {
      const token = await getToken();
      if (!token) {
        setCsvAutoEstimateError('You must be logged in.');
        return;
      }
      const response = await axios.post(
        `${API_BASE_URL}/lca/auto-estimate`,
        { product_ids: importedIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCsvAutoEstimateResult(response.data?.summary || null);
      clearSupplierCache(supplierInfo?.supplier_id);
      fetchProductsBySupplier(supplierInfo?.supplier_id, 1, searchQuery, selectedCategory, selectedSubcategory, selectedFootprint, false);
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === 'PLAN_UPGRADE_REQUIRED') {
        setCsvAutoEstimateError('AI auto-estimate requires a paid plan.');
      } else {
        setCsvAutoEstimateError(data?.error || 'Auto-estimate failed. Please try again.');
      }
    } finally {
      setCsvAutoEstimating(false);
    }
  };

  const handleCsvImport = async () => {
    if (!csvFile) return;
    setCsvImporting(true);
    setCsvError('');
    setCsvResult(null);
    try {
      const token = await getToken();
      if (!token) {
        setCsvError('You must be logged in to import products.');
        return;
      }
      const formData = new FormData();
      formData.append('file', csvFile);
      const response = await axios.post(`${API_BASE_URL}/products/import-csv`, formData, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setCsvResult(response.data);
      if (response.data.imported > 0) {
        clearSupplierCache(supplierInfo?.supplier_id);
        setPage(1);
        fetchProductsBySupplier(supplierInfo?.supplier_id, 1, searchQuery, selectedCategory, selectedSubcategory, selectedFootprint, false);
        fetchCategories(supplierInfo?.supplier_id);
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === 'PLAN_UPGRADE_REQUIRED') {
        setCsvError('CSV import is available on all paid plans.');
      } else {
        setCsvError(data?.error || 'Failed to import CSV. Please check your file and try again.');
      }
    } finally {
      setCsvImporting(false);
    }
  };

  // Handle product selection
  const handleProductSelect = (productId, event) => {
    event.stopPropagation();
    setSelectedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  // Handle select all
  const handleSelectAll = (event) => {
    event.stopPropagation();
    if (event.target.checked) {
      const allProductIds = products
        .map((p) => p.id)
        .filter(Boolean);
      setSelectedProducts(new Set(allProductIds));
    } else {
      setSelectedProducts(new Set());
    }
  };

  // Get product ID helper
  const getProductId = (product) => {
    return product.id;
  };

  // Get product name helper
  const getProductName = (product) => {
    return product.name || 'Unknown';
  };

  // Handle bulk delete products
  const handleBulkDelete = async () => {
    if (selectedProducts.size === 0) return;

    const selectedProductObjects = products.filter(p => selectedProducts.has(getProductId(p)));
    const linkedProducts = selectedProductObjects.filter(p => p.link_id);
    const ownedProducts = selectedProductObjects.filter(p => !p.link_id);

    if (ownedProducts.length > 0 && !canDeleteProducts) {
      setError('Deleting products is not available on your current plan. Upgrade to remove products.');
      return;
    }

    const productNames = selectedProductObjects.map(p => getProductName(p));
    const count = selectedProducts.size;
    const verb = linkedProducts.length === count ? 'remove' : 'delete';
    const title = count === 1
      ? `${verb === 'remove' ? 'Remove' : 'Delete'} "${productNames[0]}"?`
      : `${verb === 'remove' ? 'Remove' : 'Delete'} ${count} products?`;

    const ok = await confirm({
      title,
      message: 'This cannot be undone.',
      confirmLabel: verb === 'remove' ? 'Remove' : 'Delete',
      tone: 'danger',
    });
    if (!ok) return;

    setDeleting(true);
    setError('');

    try {
      const token = await getToken();
      if (!token) {
        setError('You must be logged in to delete products');
        setDeleting(false);
        return;
      }

      const promises = [
        ...linkedProducts.map(p =>
          axios.delete(`${API_BASE_URL}/distributor/links/${p.link_id}`, {
            headers: { 'Authorization': `Bearer ${token}` },
          })
        ),
        ...ownedProducts.map(p =>
          axios.delete(`${API_BASE_URL}/products/${p.id}`, {
            headers: { 'Authorization': `Bearer ${token}` },
          })
        ),
      ];

      await Promise.all(promises);

      // Clear all cache for this supplier
      clearSupplierCache(supplierInfo?.supplier_id);
      
      // Clear selection
      setSelectedProducts(new Set());
      
      // If we're on a page that might become empty, go to page 1
      // Otherwise stay on current page
      const currentPage = products.length <= selectedProducts.size && page > 1 ? Math.max(1, page - 1) : page;
      setPage(currentPage);
      
      // Refresh product list
      fetchProductsBySupplier(supplierInfo?.supplier_id, currentPage, searchQuery, selectedCategory, selectedSubcategory, selectedFootprint, false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete products');
      console.error('Error deleting products:', err);
    } finally {
      setDeleting(false);
    }
  };

  // Distributor: fork a linked product into an owned product so it can be assessed.
  const handleForkLinkedProduct = async (product) => {
    if (!product?.link_id) return;
    const ok = await confirm({
      title: `Add "${product.name || 'this product'}" to your catalogue?`,
      message: `You'll be able to run your own carbon footprint assessment on it. The link to ${product.source_supplier?.name || 'the supplier'} will be removed.`,
      confirmLabel: 'Add to my catalogue',
    });
    if (!ok) return;
    try {
      const res = await axios.post(`${API_BASE_URL}/distributor/links/${product.link_id}/fork`);
      const newProductId = res.data?.product?.id;
      clearSupplierCache(supplierInfo?.supplier_id);
      if (newProductId) {
        // Navigate with product data as router state so LCACalculator doesn't
        // get stuck waiting for a product that was never passed.
        navigate(`/lca-calculator/${newProductId}`, {
          state: { product: res.data?.product },
        });
      } else {
        await fetchProductsBySupplier(supplierInfo?.supplier_id, page, searchQuery, selectedCategory, selectedSubcategory, selectedFootprint, false);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fork product');
    }
  };

  // ── Request PCF from the source supplier (linked products) ──────────
  const openPcfRequestModal = (product) => {
    setPcfRequestProduct(product);
    setPcfRequestQuantity("");
    setPcfRequestReason("");
    setPcfRequestError("");
    setPcfRequestSuccess("");
  };

  const closePcfRequestModal = () => {
    if (pcfRequestSubmitting) return;
    setPcfRequestProduct(null);
    setPcfRequestError("");
    setPcfRequestSuccess("");
  };

  // Flag the product as requested in the list + cache so the row switches to
  // "PCF requested" without a refetch.
  const markProductRequested = (productId) => {
    setProducts((prev) => {
      const next = prev.map((p) =>
        getProductId(p) === productId ? { ...p, pcf_requested: true } : p,
      );
      updateCurrentCache(next);
      return next;
    });
  };

  const submitPcfRequest = async (e) => {
    e.preventDefault();
    if (!pcfRequestProduct || pcfRequestSubmitting) return;
    const qty = parseInt(pcfRequestQuantity, 10);
    if (!Number.isFinite(qty) || qty <= 0) {
      setPcfRequestError("Enter a quantity (whole number greater than 0).");
      return;
    }
    if (!pcfRequestReason.trim()) {
      setPcfRequestError("Add a short reason so the supplier knows the context.");
      return;
    }
    setPcfRequestSubmitting(true);
    setPcfRequestError("");
    const supplierName = pcfRequestProduct.source_supplier?.name || "the supplier";
    try {
      const res = await axios.post(`${API_BASE_URL}/distributor/pcf-requests`, {
        source_product_id: pcfRequestProduct.id,
        quantity: qty,
        reason: pcfRequestReason.trim(),
      });
      markProductRequested(pcfRequestProduct.id);
      setPcfRequestSuccess(
        res.data?.email_sent
          ? `Request sent to ${supplierName}. They will reply directly to you.`
          : res.data?.supplier_has_email === false
            ? `Request saved. ${supplierName} hasn't claimed their Zilch account yet, so we couldn't email them — we'll surface it next time they sign in.`
            : "Request saved. We'll try the email again shortly.",
      );
    } catch (err) {
      // A duplicate isn't a failure — the supplier already has the request.
      if (err.response?.status === 409) {
        markProductRequested(pcfRequestProduct.id);
        setPcfRequestSuccess(
          `You've already requested a PCF for this product from ${supplierName}.`,
        );
      } else {
        setPcfRequestError(err.response?.data?.error || "Failed to send request");
      }
    } finally {
      setPcfRequestSubmitting(false);
    }
  };

  // Open a product in the modal with the "add missing data" prompt active, so a
  // blocked auto-estimate lands the user on the fix (the weight field) rather
  // than an error banner they may never scroll up to see.
  const openProductForDataFix = (product) => {
    setEditingProduct(product);
    setProductModalMode("view");
    setProductModalNeedsData(true);
    setShowProductModal(true);
  };

  // Run an AI auto-estimate directly from the product list, mirroring the
  // "Auto-estimate PCF" action in ProductModal. Linked products are forked
  // into the supplier's own catalogue first so they can be assessed.
  const handleAutoEstimateProduct = async (product) => {
    // If required data (weight) is missing, don't fire a request that will fail
    // with an off-screen error — open the product so the user can add it here.
    const preCheck = getProductDataReadiness(product);
    if (preCheck.blocked) {
      openProductForDataFix(product);
      return;
    }

    let target = product;
    if (product.link_status === 'linked' && product.link_id) {
      const supplierName = product.source_supplier?.name || 'the supplier';
      const ok = await confirm({
        title: 'Create your own copy?',
        message: `This product is linked from ${supplierName}. Running your own assessment will create your own copy of it — if ${supplierName} later publishes their own PCF, you'll get the chance to update your assessment with their version.`,
        confirmLabel: 'Create my copy',
      });
      if (!ok) return;
      try {
        const res = await axios.post(`${API_BASE_URL}/distributor/links/${product.link_id}/fork`);
        target = res.data?.product;
        if (!target) {
          setError('Failed to copy product into your catalogue.');
          return;
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to copy product into your catalogue.');
        return;
      }
    }

    const hasExisting = Number(target?.lca?.current_step || 0) >= 1;
    if (hasExisting) {
      const ok = await confirm({
        title: 'Replace the existing assessment?',
        message: 'A PCF agent will generate a new estimate from the data provided and replace this product\'s current assessment.',
        confirmLabel: 'Replace assessment',
      });
      if (!ok) return;
    }

    // Warn (but don't block) when advisory data is thin — weight (the hard gate)
    // is handled by the readiness pre-check above, so the remaining gaps here
    // just reduce accuracy rather than prevent the estimate.
    const { recommendedMissing } = getProductDataReadiness(target);
    if (recommendedMissing.length > 0) {
      const ok = await confirm({
        title: 'Some data is missing',
        message: `This product has no ${recommendedMissing.join(', ')}, so the AI estimate will be less accurate. You can add the missing details first, or run it anyway.`,
        confirmLabel: 'Run it anyway',
        cancelLabel: 'Add the details',
      });
      if (!ok) return;
    }

    setAutoEstimatingId(product.id);
    setError('');
    try {
      const token = await getToken();
      if (!token) { setError('You must be logged in.'); return; }

      const response = await axios.post(
        `${API_BASE_URL}/lca/auto-estimate`,
        { product_ids: [target.id], overwrite: hasExisting },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const result = response.data?.results?.[0];
      if (!result) { setError('Unexpected response from server.'); return; }
      if (result.status === 'estimated') {
        navigate(`/lca-calculator/${target.id}`, { state: { product: target } });
        return;
      }
      if (result.status === 'needs_input') {
        // Required data is missing — route the user to the modal to add it,
        // instead of an error banner above the (scrolled-out-of-view) table.
        openProductForDataFix(target);
        return;
      }
      if (result.status === 'skipped_exists') {
        setError('An assessment already exists for this product.');
      } else {
        setError(result.reason || 'Auto-estimate failed.');
      }
      if (target.id !== product.id) {
        clearSupplierCache(supplierInfo?.supplier_id);
        fetchProductsBySupplier(supplierInfo?.supplier_id, page, searchQuery, selectedCategory, selectedSubcategory, selectedFootprint, false);
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === 'PLAN_UPGRADE_REQUIRED') {
        setError('AI auto-estimate requires a paid plan.');
      } else {
        setError(data?.error || 'Auto-estimate failed. Please try again.');
      }
    } finally {
      setAutoEstimatingId(null);
    }
  };

  // What a bulk AI run would cover: the products selected on the current page,
  // capped at one run's worth. Linked products (a distributor's copy of someone
  // else's SKU) have to be forked before they can be assessed, and products past
  // the plan's assessment cap can't be assessed at all — both are held back here
  // and reported in the modal rather than silently dropped.
  const buildBulkAiSelection = () => {
    const selected = products.filter((p) => selectedProducts.has(getProductId(p)));
    const linked = selected.filter((p) => p.link_status === "linked");
    const rest = selected.filter((p) => p.link_status !== "linked");
    const capped = rest.filter((p) => shouldShowUpgradeForProduct(p));
    const eligible = rest.filter((p) => !shouldShowUpgradeForProduct(p));
    const batch = eligible.slice(0, BULK_AI_MAX_PER_RUN);
    return {
      items: batch.map((p) => {
        const readiness = getProductDataReadiness(p);
        return {
          id: p.id,
          name: getProductName(p),
          code: p.code || "",
          blocked: readiness.blocked,
          missing: [...readiness.requiredMissing, ...readiness.recommendedMissing],
          hasExisting: hasStartedAssessment(p),
        };
      }),
      excluded: {
        linked: linked.length,
        limitReached: capped.length,
        overflow: Math.max(0, eligible.length - batch.length),
      },
    };
  };

  const handleUnlinkAssessment = async (targetProduct) => {
    const p = targetProduct || selectedProduct;
    if (!p?.lca?.is_linked) return;

    const productId = p?.id;
    const productName = p?.name || "this product";

    const ok = await confirm({
      title: `Unlink the assessment for "${productName}"?`,
      message: 'This product will then be able to be assessed independently.',
      confirmLabel: 'Unlink assessment',
    });
    if (!ok) return;

    setUnlinking(true);
    setError("");

    try {
      const token = await getToken();
      if (!token) {
        setError("You must be logged in to unlink assessments");
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/lca/assessments/unmatch`,
        { product_ids: [productId] },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const results = Array.isArray(response?.data?.results) ? response.data.results : [];
      const allOk = response?.data?.success === true && results.every((r) => r && r.success === true);
      if (!allOk) {
        const message =
          response?.data?.error ||
          results.find((r) => r && r.error)?.error ||
          "Failed to unlink assessment";
        setError(message);
        return;
      }

      setProducts((prev) => {
        const next = prev.map((product) => {
          if (getProductId(product) !== productId) return product;
          return {
            ...product,
            lca: {
              ...(product.lca || {}),
              is_linked: false,
              source_assessment_id: null,
              total_emissions_kg: null,
              current_step: null,
              parent_category_slug: null,
            },
          };
        });
        updateCurrentCache(next);
        return next;
      });

      if (selectedProduct && getProductId(selectedProduct) === productId) {
        setSelectedProduct({
          ...selectedProduct,
          lca: {
            ...(selectedProduct.lca || {}),
            is_linked: false,
            source_assessment_id: null,
            total_emissions_kg: null,
            current_step: null,
            parent_category_slug: null,
          },
        });
      }

      clearSupplierCache(supplierInfo?.supplier_id);
      await fetchProductsBySupplier(
        supplierInfo?.supplier_id,
        page,
        searchQuery,
        selectedCategory,
        selectedSubcategory,
        selectedFootprint,
        false
      );

      setShowProductModal(false);
      setEditingProduct(null);

      await alert({
        title: 'Assessment unlinked',
        message: 'You can now assess this SKU independently.',
      });
    } catch (err) {
      console.error("Error unlinking assessment:", err);
      setError(err.response?.data?.error || "Failed to unlink assessment");
    } finally {
      setUnlinking(false);
    }
  };

  return (
    <div className="p-3">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div>
              <h1 className="fw-bold mb-2">Your Products</h1>
              <p className="text-muted">
                Calculate and manage carbon footprints across your product
                library.
              </p>
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {hasAssessmentCap && (
                <div className="sku-pill text-muted">
                  {assessmentUsed}/{assessmentLimitRaw} assessments
                </div>
              )}
              {isDistributor && (
                <Link
                  to="/suppliers"
                  className="zilch-green-button text-decoration-none"
                >
                  Browse Suppliers
                </Link>
              )}
              {/* Add products dropdown */}
              <div className="dropdown">
                <button
                  className="zilch-black-button dropdown-toggle"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  + Add Products
                </button>
                <ul className="dropdown-menu dropdown-menu-end">
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => {
                        setProductModalMode("create");
                        setEditingProduct(null);
                        setProductModalNeedsData(false);
                        setShowProductModal(true);
                      }}
                    >
                      Single product
                    </button>
                  </li>
                  {canUseCsvImport && (
                    <li>
                      <button
                        className="dropdown-item"
                        onClick={() => setShowCsvModal(true)}
                      >
                        From CSV
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>

          {error && (
            <div className="alert alert-danger mb-3" role="alert">
              {error}
            </div>
          )}

          {/* Search and Filter Controls - Always visible */}
          {supplierInfo?.supplier_id && (
            <div className="filter-toolbar">
              <div className="filter-search">
                <svg
                  className="filter-search-icon"
                  viewBox="0 0 16 16"
                  fill="none"
                  aria-hidden="true"
                >
                  <circle
                    cx="7"
                    cy="7"
                    r="5"
                    stroke="currentColor"
                    strokeWidth="1.6"
                  />
                  <path
                    d="M11 11l3 3"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
                <input
                  type="search"
                  placeholder="Search by SKU, name, category…"
                  aria-label="Search products"
                  value={searchInput}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
              </div>

              

              <select
                className={`filter-select${selectedCategory !== "all" ? " is-active" : ""}`}
                aria-label="Filter by category"
                value={selectedCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
              >
                <option value="all">All categories</option>
                {usesPromoTaxonomy
                  ? categoryOptions.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))
                  : supplierCategories.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
              </select>

              {availableSubcategories.length > 0 && (
                <select
                  className={`filter-select${selectedSubcategory !== "all" ? " is-active" : ""}`}
                  aria-label="Filter by subcategory"
                  value={selectedSubcategory}
                  onChange={(e) => handleSubcategoryChange(e.target.value)}
                >
                  <option value="all">All subcategories</option>
                  {availableSubcategories.map((sub) => (
                    <option key={sub.id} value={sub.id}>
                      {sub.name}
                    </option>
                  ))}
                </select>
              )}

              <div className="filter-divider" />

              <label
                className={`filter-switch${selectedFootprint === "true" ? " is-active" : ""}`}
                htmlFor="footprintFilter"
              >
                <input
                  type="checkbox"
                  id="footprintFilter"
                  checked={selectedFootprint === "true"}
                  onChange={(e) =>
                    handleFootprintChange(e.target.checked ? "true" : "all")
                  }
                />
                Assessment completed
              </label>

              <label
                className={`filter-switch${selectedNeedsData ? " is-active" : ""}`}
                htmlFor="needsDataFilter"
                title="Products missing the weight needed for an AI assessment"
              >
                <input
                  type="checkbox"
                  id="needsDataFilter"
                  checked={selectedNeedsData}
                  onChange={(e) => {
                    setSelectedNeedsData(e.target.checked);
                    setPage(1);
                  }}
                />
                Data required
              </label>

              {isSupplier && pcfRequests.length > 0 && (
                <label
                  className={`filter-switch${showRequestedPcf ? " is-active" : ""}`}
                  htmlFor="requestedPcfFilter"
                  style={{ position: 'relative' }}
                >
                  <input
                    type="checkbox"
                    id="requestedPcfFilter"
                    checked={showRequestedPcf}
                    onChange={(e) => setShowRequestedPcf(e.target.checked)}
                  />
                  Requested PCF
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginLeft: 6,
                    minWidth: 18,
                    height: 18,
                    borderRadius: 99,
                    background: '#e53935',
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 700,
                    padding: '0 5px',
                  }}>
                    {pcfRequests.length}
                  </span>
                </label>
              )}

              

              {hasActiveFilters && (
                <button
                  type="button"
                  className="filter-clear-btn"
                  onClick={clearAllFilters}
                >
                  Clear all
                </button>
              )}
            </div>
          )}

          {/* Requested PCF view — supplier only, shown when toggle is on */}
          {showRequestedPcf && isSupplier && (
            <div className="mt-2">
              {pcfRequestsLoading ? (
                <div className="text-muted py-4 text-center" style={{ fontSize: 14 }}>Loading requests…</div>
              ) : pcfRequests.length === 0 ? (
                <div className="text-muted py-4 text-center" style={{ fontSize: 14 }}>No PCF requests yet.</div>
              ) : (
                <div className="table-responsive rounded-3">
                  <table className="table table-hover align-middle bg-white border px-3 mb-3" style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                      <col style={{ width: '22%' }} />
                      <col style={{ width: '16%' }} />
                      <col style={{ width: '10%' }} />
                      <col style={{ width: '34%' }} />
                      <col style={{ width: '10%' }} />
                      <col style={{ width: '8%' }} />
                    </colgroup>
                    <thead className="table-light">
                      <tr>
                        <th>Product</th>
                        <th>Requested by</th>
                        <th>Qty</th>
                        <th>Reason</th>
                        <th>Received</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {pcfRequests.map(req => (
                        <tr key={req.id}>
                          <td style={{ overflow: 'hidden' }}>
                            <div style={{
                              fontWeight: 600,
                              fontSize: 13,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }} title={req.product_name_snapshot}>
                              {req.product_name_snapshot || '—'}
                            </div>
                            {req.product_code_snapshot && (
                              <div className="small text-muted">{req.product_code_snapshot}</div>
                            )}
                          </td>
                          <td style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={req.distributor_name}>
                            {req.distributor_name}
                          </td>
                          {/* Null for a catalogue-wide request, where the
                              distributor was never asked for a per-SKU number. */}
                          <td style={{ fontSize: 13 }}>
                            {Number.isFinite(Number(req.quantity)) && req.quantity !== null
                              ? Number(req.quantity).toLocaleString()
                              : '—'}
                          </td>
                          <td>
                            <div style={{
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              fontSize: 13,
                              color: '#374151',
                              lineHeight: 1.4,
                            }} title={req.reason}>
                              {req.reason}
                            </div>
                          </td>
                          <td className="small text-muted" style={{ whiteSpace: 'nowrap' }}>
                            {new Date(req.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                          </td>
                          <td>
                            <Link
                              to={`/lca-calculator/${req.source_product_id}`}
                              state={{ product: { id: req.source_product_id, name: req.product_name_snapshot, code: req.product_code_snapshot } }}
                              className="btn btn-outline-success btn-sm"
                            >
                              Calculate
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Products Table */}
          {!showRequestedPcf && loading && (
            <div>
              {/* Pagination controls (top) */}
              <div className="d-flex justify-content-end align-items-center gap-2 mb-2">
                <div className="skeleton-text" style={{ width: "100px" }}></div>
                <div className="btn-group" role="group" aria-label="Pagination">
                  <button className="btn btn-outline-secondary btn-sm" disabled>
                    Prev
                  </button>
                  <button className="btn btn-outline-secondary btn-sm" disabled>
                    Next
                  </button>
                </div>
              </div>

              <TableSkeleton rows={5} />
            </div>
          )}

          {!showRequestedPcf && !loading && products.length > 0 && (
            <div>
              {/* Bulk actions and pagination controls (top) */}
              <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                <div className="d-flex gap-2">
                  {selectedProducts.size > 0 && (
                    <>
                      {/* Bulk AI assessment for the current page's selection.
                          Free plans get the upsell modal, same as the per-row button. */}
                      {isFreePlan ? (
                        <button
                          type="button"
                          className="btn btn-ai-assess btn-sm"
                          onClick={() => setShowAiAssessModal(true)}
                          title="See how AI can assess your products for you"
                        >
                          ✨ AI Assess {selectedProducts.size}
                        </button>
                      ) : (
                        canEditProducts && (
                          <button
                            type="button"
                            className="btn btn-ai-assess btn-sm"
                            onClick={() => setShowBulkAiModal(true)}
                            title={`Use AI to assess the ${selectedProducts.size} selected product${selectedProducts.size === 1 ? "" : "s"}`}
                          >
                            ✨ AI Assess {selectedProducts.size}
                          </button>
                        )
                      )}
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => setShowMatchModal(true)}
                      >
                        Match Assessment
                      </button>
                      {canDeleteProducts && (
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={handleBulkDelete}
                          disabled={deleting}
                          title={
                            !canDeleteProducts
                              ? "Upgrade to delete products"
                              : undefined
                          }
                        >
                          {deleting
                            ? "Deleting..."
                            : `Delete ${selectedProducts.size} Selected`}
                        </button>
                      )}
                    </>
                  )}
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted" style={{ fontSize: 13 }}>
                    Page {page} of {Math.max(1, totalPages)}
                  </span>
                  <div
                    className="btn-group"
                    role="group"
                    aria-label="Pagination"
                  >
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handlePrev}
                      disabled={page <= 1 || loading}
                    >
                      Prev
                    </button>
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={handleNext}
                      disabled={page >= (totalPages || 1) || loading}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>

              <div className="table-responsive rounded-3">
                <table className="table table-hover align-middle bg-white border  px-3 mb-3 product-table">
                  <thead className="table-light">
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          className="product-checkbox"
                          checked={
                            products.length > 0 &&
                            products.every((p) =>
                              selectedProducts.has(getProductId(p)),
                            )
                          }
                          onChange={handleSelectAll}
                          onClick={(e) => e.stopPropagation()}
                          aria-label="Select all products"
                        />
                      </th>
                      <th className="col-image">Image</th>
                      <th className="col-sku">SKU</th>
                      <th className="col-name">Product Name</th>
                      <th className="col-category">Category</th>
                      <th className="col-data">Data</th>
                      <th className="col-footprint">Carbon Footprint</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product, idx) => {
                      const productId = getProductId(product);
                      const isSelected = selectedProducts.has(productId);
                      return (
                        <React.Fragment
                          key={productId || product.id || product.code || idx}
                        >
                          <tr
                            role="button"
                            tabIndex={0}
                            style={{ cursor: "pointer" }}
                            className={isSelected ? "table-active" : ""}
                            onClick={() => {
                              setEditingProduct(product);
                              setProductModalMode("view");
                              setProductModalNeedsData(false);
                              setShowProductModal(true);
                            }}
                          >
                            <td
                              onClick={(e) => e.stopPropagation()}
                              style={{ textAlign: "center" }}
                            >
                              <input
                                type="checkbox"
                                className="product-checkbox"
                                checked={isSelected}
                                onChange={(e) =>
                                  handleProductSelect(productId, e)
                                }
                                onClick={(e) => e.stopPropagation()}
                                aria-label={`Select ${getProductName(product)}`}
                              />
                            </td>
                            <td className="col-image">
                              <img
                                className="img-fluid rounded"
                                height={40}
                                width={40}
                                src={
                                  product.images?.[0] ||
                                  supplierInfo?.logo_url ||
                                  zilchLogoBlack
                                }
                                alt={product.name || "N/A"}
                                onError={(e) => {
                                  e.currentTarget.src =
                                    supplierInfo?.logo_url || zilchLogoBlack;
                                }}
                              />
                            </td>
                            <td className="col-sku">
                              <strong>{product.code || "N/A"}</strong>
                            </td>
                            <td className="col-name">
                              <div>{product.name || "N/A"}</div>
                              {product.link_status === "linked" && (
                                <div className="small text-muted mt-1">
                                  <span className="badge bg-info-subtle text-info border me-1">
                                    Linked
                                  </span>
                                  from{" "}
                                  {product.source_supplier?.name || "supplier"}
                                </div>
                              )}
                            </td>

                            <td className="col-category">
                              <span className="badge rounded-pill bg-light text-dark border">
                                {product.promo_category?.name ||
                                  product.supplier_category ||
                                  product?.lca?.parent_category_slug ||
                                  "N/A"}
                              </span>
                              {/* {product.promo_subcategory?.name && (
                              <div className="small text-muted mt-1">{product.promo_subcategory.name}</div>
                            )} */}
                            </td>
                            <td className="col-data">
                              {(() => {
                                // Once the assessment is complete, the data has done
                                // its job — show nothing. Otherwise: Ready when nothing
                                // is missing, Incomplete (with a tooltip of what's
                                // missing) when any field the modal tracks is absent.
                                if (hasViewableResults(product)) return null;
                                const { ready, requiredMissing, recommendedMissing } =
                                  getProductDataReadiness(product);
                                if (ready) {
                                  return (
                                    <span
                                      className="badge bg-success-subtle text-success border"
                                      title="Has the data needed for an assessment"
                                    >
                                      Ready
                                    </span>
                                  );
                                }
                                const missing = [...requiredMissing, ...recommendedMissing].join(", ");
                                return (
                                  <span
                                    className="badge bg-warning-subtle text-warning-emphasis border"
                                    title={`Missing: ${missing}`}
                                  >
                                    Incomplete
                                  </span>
                                );
                              })()}
                            </td>
                            {/* Carbon footprint and the way to get one, in one
                                column: the figure when it exists, otherwise the
                                Assess popover. */}
                            <td
                              className="col-footprint"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {renderAssessmentCell(product)}
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination controls (bottom) */}
              <div className="d-flex justify-content-between align-items-center mt-2">
                <span className="text-muted" style={{ fontSize: 12 }}>
                  Showing {products.length} of {totalCount}
                </span>
                <div className="btn-group" role="group" aria-label="Pagination">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={handlePrev}
                    disabled={page <= 1 || loading}
                  >
                    Prev
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={handleNext}
                    disabled={page >= (totalPages || 1) || loading}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}

          {!showRequestedPcf && !loading && products.length === 0 && supplierInfo?.supplier_id && (
            <div className="text-center text-muted py-5">
              <p>No products found. Try adjusting your search or filters.</p>
            </div>
          )}
        </div>
      </div>
      {/* Product Modal (create + view/edit) */}
      <ProductModal
        mode={productModalMode}
        show={showProductModal}
        onClose={() => {
          setShowProductModal(false);
          setEditingProduct(null);
          setProductModalNeedsData(false);
        }}
        onSuccess={() => {
          setShowProductModal(false);
          setEditingProduct(null);
          setProductModalNeedsData(false);
          clearSupplierCache(supplierInfo?.supplier_id);
          setPage(1);
          fetchCategories(supplierInfo?.supplier_id);
          fetchProductsBySupplier(
            supplierInfo?.supplier_id,
            1,
            searchQuery,
            selectedCategory,
            selectedSubcategory,
            selectedFootprint,
            false,
          );
        }}
        product={editingProduct}
        highlightMissingData={productModalNeedsData}
        supplierInfo={supplierInfo}
        getToken={getToken}
        promoCategories={promoCategories}
        usesPromoTaxonomy={usesPromoTaxonomy}
        supplierCategoryOptions={supplierCategories}
        canEditProducts={canEditProducts}
        assessmentLimitReached={assessmentLimitReached}
        isFreePlan={isFreePlan}
        onAiAssess={() => setShowAiAssessModal(true)}
        onUnlink={handleUnlinkAssessment}
        unlinking={unlinking}
      />

      {/* AI Assess upsell modal (free plan) */}
      <AiAssessUpsellModal
        show={showAiAssessModal}
        onClose={() => setShowAiAssessModal(false)}
      />

      {/* Bulk AI assessment for the selected products on this page */}
      {showBulkAiModal && (() => {
        const { items, excluded } = buildBulkAiSelection();
        return (
          <BulkAiAssessModal
            show
            items={items}
            excluded={excluded}
            maxPerRun={BULK_AI_MAX_PER_RUN}
            canUsePartsMode={canUsePartsAiAssessment}
            getToken={getToken}
            onClose={() => setShowBulkAiModal(false)}
            onCompleted={() => {
              clearSupplierCache(supplierInfo?.supplier_id);
              fetchProductsBySupplier(
                supplierInfo?.supplier_id,
                page,
                searchQuery,
                selectedCategory,
                selectedSubcategory,
                selectedFootprint,
                false,
              );
            }}
          />
        );
      })()}

      {/* CSV Import Modal */}
      <div
        className={`modal fade ${showCsvModal ? "show" : ""}`}
        tabIndex="-1"
        role="dialog"
        aria-labelledby="csvImportModalTitle"
        aria-hidden={!showCsvModal}
        style={{ display: showCsvModal ? "block" : "none" }}
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="csvImportModalTitle">
                Import Products from CSV
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={closeCsvModal}
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              {!csvResult && (
                <>
                  <p className="text-muted small mb-3">
                    Upload a CSV file to import multiple products at once.
                    Download the template to see the required format.
                  </p>
                  <div className="mb-3">
                    <a
                      href={generateCsvTemplate()}
                      download="zilch-product-import-template.csv"
                      className="btn btn-outline-secondary btn-sm"
                    >
                      Download Template
                    </a>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="csvFileInput" className="form-label">
                      Select CSV file <span className="text-danger">*</span>
                    </label>
                    <input
                      type="file"
                      id="csvFileInput"
                      className="form-control"
                      accept=".csv,text/csv"
                      onChange={(e) => {
                        setCsvFile(e.target.files?.[0] || null);
                        setCsvError("");
                        setCsvResult(null);
                      }}
                      disabled={csvImporting}
                    />
                  </div>
                  {csvError && (
                    <div className="alert alert-danger py-2 small" role="alert">
                      {csvError}
                    </div>
                  )}
                </>
              )}
              {csvResult && (
                <div>
                  {csvResult.imported > 0 && (
                    <div className="alert alert-success py-2 small">
                      {csvResult.imported} product
                      {csvResult.imported !== 1 ? "s" : ""} imported
                      successfully.
                      {csvResult.categorizing && (
                        <div className="mt-1 text-muted">
                          Categorising in the background, refresh in a minute to
                          see them sorted.
                        </div>
                      )}
                    </div>
                  )}
                  {csvResult.readiness && csvResult.imported > 0 && (
                    <div
                      className={`alert py-2 small ${csvResult.readiness.needs_input > 0 ? "alert-warning" : "alert-success"}`}
                    >
                      {csvResult.readiness.ready} of {csvResult.imported} ready
                      for an AI assessment.
                      {csvResult.readiness.needs_input > 0 && (
                        <>
                          {" "}
                          {csvResult.readiness.needs_input} missing a weight —
                          use the “Needs data” filter on the product list to
                          find and complete them. (AI may fill some gaps from
                          descriptions in the next few minutes.)
                        </>
                      )}
                    </div>
                  )}
                  {csvResult.skipped > 0 && (
                    <div className="alert alert-warning py-2 small">
                      {csvResult.skipped} row
                      {csvResult.skipped !== 1 ? "s" : ""} skipped due to
                      errors.
                    </div>
                  )}
                  {csvResult.errors?.length > 0 && (
                    <div style={{ maxHeight: 200, overflowY: "auto" }}>
                      <p className="small fw-semibold text-muted mb-1">
                        Row errors:
                      </p>
                      <ul className="list-unstyled small">
                        {csvResult.errors.map((e, i) => (
                          <li key={i} className="mb-1">
                            <strong>Row {e.row}</strong>
                            {e.name ? ` (${e.name})` : ""}:{" "}
                            {e.errors.join("; ")}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {csvResult.imported > 0 && !csvAutoEstimateResult && (
                    <div className="border-top pt-3 mt-3">
                      <p className="small fw-semibold mb-1">
                        AI product carbon footprint estimates (optional)
                      </p>
                      <p className="small text-muted mb-2">
                        Try to auto-match each product to a finished-product
                        assessment and pre-fill its PCF. You can refine each one
                        before locking it in. You can also skip this step and
                        run estimates later from the product page.
                      </p>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={handleCsvAutoEstimate}
                        disabled={csvAutoEstimating}
                      >
                        {csvAutoEstimating
                          ? `Estimating ${csvResult.imported}…`
                          : `Run PCF Agent (${csvResult.imported})`}
                      </button>
                      {csvAutoEstimateError && (
                        <div className="alert alert-danger py-2 small mt-2 mb-0">
                          {csvAutoEstimateError}
                        </div>
                      )}
                    </div>
                  )}

                  {csvAutoEstimateResult && (
                    <div className="alert alert-info py-2 small mt-3">
                      The PCF agent completed{" "}
                      <strong>{csvAutoEstimateResult.estimated || 0}</strong>{" "}
                      footprint assessment
                      {csvAutoEstimateResult.estimated !== 1 ? "s" : ""} using
                      the data provided
                      {csvAutoEstimateResult.skipped_exists > 0 && (
                        <>
                          , {csvAutoEstimateResult.skipped_exists} product
                          {csvAutoEstimateResult.skipped_exists !== 1
                            ? "s already had"
                            : " already had"}{" "}
                          an assessment
                        </>
                      )}
                      {csvAutoEstimateResult.needs_input > 0 && (
                        <>
                          , {csvAutoEstimateResult.needs_input} skipped because
                          the weight is missing (find them with the “Needs
                          data” filter)
                        </>
                      )}
                      {csvAutoEstimateResult.error > 0 && (
                        <>
                          , {csvAutoEstimateResult.error} error
                          {csvAutoEstimateResult.error !== 1 ? "s" : ""}{" "}
                          occurred
                        </>
                      )}
                      . Open each product to review and confirm the assessment
                      before using it.
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeCsvModal}
                disabled={csvImporting}
              >
                {csvResult ? "Close" : "Cancel"}
              </button>
              {!csvResult && (
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleCsvImport}
                  disabled={!csvFile || csvImporting}
                >
                  {csvImporting ? "Importing..." : "Import"}
                </button>
              )}
              {csvResult && csvResult.imported === 0 && (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setCsvResult(null);
                    setCsvFile(null);
                    setCsvError("");
                  }}
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      {showCsvModal && (
        <div className="modal-backdrop fade show" onClick={closeCsvModal} />
      )}

      {/* Assess options popover for the row whose trigger is open */}
      {assessMenu && (() => {
        const product = products.find((p) => getProductId(p) === assessMenu.productId);
        if (!product) return null;
        return (
          <AssessMenu
            anchorEl={assessMenu.anchorEl}
            items={buildAssessMenuItems(product)}
            onClose={closeAssessMenu}
          />
        );
      })()}

      {/* Request PCF from the source supplier (linked products) */}
      {pcfRequestProduct && (
        <>
          <div
            className="modal fade show"
            tabIndex="-1"
            role="dialog"
            aria-labelledby="pcfRequestModalTitle"
            style={{ display: "block" }}
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="pcfRequestModalTitle">
                    Request PCF from{" "}
                    {pcfRequestProduct.source_supplier?.name || "the supplier"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closePcfRequestModal}
                    disabled={pcfRequestSubmitting}
                    aria-label="Close"
                  />
                </div>

                {pcfRequestSuccess ? (
                  <>
                    <div className="modal-body">
                      <div className="alert alert-success mb-0">
                        {pcfRequestSuccess}
                      </div>
                      <p className="small text-muted mt-3 mb-0">
                        This product stays in your catalogue — the footprint will
                        appear here automatically once the assessment is complete.
                      </p>
                    </div>
                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={closePcfRequestModal}
                      >
                        Done
                      </button>
                    </div>
                  </>
                ) : (
                  <form onSubmit={submitPcfRequest}>
                    <div className="modal-body">
                      <p className="text-muted small">
                        We'll email{" "}
                        <strong>
                          {pcfRequestProduct.source_supplier?.name || "the supplier"}
                        </strong>{" "}
                        on your behalf to request PCF data for{" "}
                        <strong>{pcfRequestProduct.name}</strong>
                        {pcfRequestProduct.code ? ` (${pcfRequestProduct.code})` : ""}.
                        Once they complete the assessment you'll see the footprint on
                        this product.
                      </p>

                      <label className="form-label small fw-semibold" htmlFor="pcf-request-quantity">
                        Quantity you'll be selling <span className="text-danger">*</span>
                      </label>
                      <input
                        id="pcf-request-quantity"
                        type="number"
                        min="1"
                        step="1"
                        required
                        className="form-control"
                        value={pcfRequestQuantity}
                        onChange={(e) => setPcfRequestQuantity(e.target.value)}
                        placeholder="e.g. 1000"
                      />

                      <label className="form-label small fw-semibold mt-3" htmlFor="pcf-request-reason">
                        Reason for the request <span className="text-danger">*</span>
                      </label>
                      <textarea
                        id="pcf-request-reason"
                        required
                        rows={4}
                        maxLength={4000}
                        className="form-control"
                        value={pcfRequestReason}
                        onChange={(e) => setPcfRequestReason(e.target.value)}
                        placeholder="e.g. Submitting a bid to the Australian Open and need a verified PCF for this product."
                      />
                      <div className="small text-muted text-end mt-1">
                        {pcfRequestReason.length}/4000
                      </div>

                      {pcfRequestError && (
                        <div className="alert alert-danger mt-3 mb-0 py-2 small">
                          {pcfRequestError}
                        </div>
                      )}
                    </div>
                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={closePcfRequestModal}
                        disabled={pcfRequestSubmitting}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-success"
                        disabled={pcfRequestSubmitting}
                      >
                        {pcfRequestSubmitting ? "Sending…" : "Send request"}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={closePcfRequestModal} />
        </>
      )}

      {/* Assessment Match Modal */}
      <AssessmentMatchModal
        show={showMatchModal}
        onClose={() => setShowMatchModal(false)}
        selectedProducts={products.filter((p) =>
          selectedProducts.has(getProductId(p)),
        )}
        onSuccess={({
          productIds = [],
          sourceAssessmentId,
          sourceAssessment,
        } = {}) => {
          const productIdSet = new Set(productIds || []);
          setSelectedProducts(new Set());
          setProducts((prev) => {
            const next = prev.map((product) => {
              if (!productIdSet.has(getProductId(product))) return product;
              return {
                ...product,
                lca: {
                  ...(product.lca || {}),
                  is_linked: true,
                  source_assessment_id:
                    sourceAssessmentId ||
                    product.lca?.source_assessment_id ||
                    null,
                  total_emissions_kg:
                    sourceAssessment?.total_emissions_kg ??
                    product.lca?.total_emissions_kg ??
                    null,
                  current_step:
                    sourceAssessment?.current_step ??
                    product.lca?.current_step ??
                    null,
                  parent_category_slug:
                    sourceAssessment?.parent_category_slug ??
                    product.lca?.parent_category_slug ??
                    null,
                },
              };
            });
            updateCurrentCache(next);
            return next;
          });
          clearSupplierCache(supplierInfo?.supplier_id);
          fetchProductsBySupplier(
            supplierInfo?.supplier_id,
            page,
            searchQuery,
            selectedCategory,
            selectedSubcategory,
            selectedFootprint,
            false,
          );
        }}
        supplierId={supplierInfo?.supplier_id}
        getToken={getToken}
      />
    </div>
  );
}

export default ProductList;
